import type { Attendee, Organizer } from 'models';
import { useCallback } from 'react';
import { EventContent, EventSocialContent } from 'features/events';
import {
  useGetRsvpFromAttandee,
  useMapPrincipalCalendarCategoryToEventType,
  useMapPrincipalCalendarEventStatus,
} from '../../hooks';
import type { EventItem } from '../Agenda';
import { useGetSplitId } from './useGetSplitId';

const createEventModerator = (organizer: Organizer) => ({
  ref: organizer.id,
  role: 'moderator' as const,
  rsvp: 'going' as const,
});

export const useCreateEventExtra = () => {
  const mapPrincipalCalendarEventStatus = useMapPrincipalCalendarEventStatus();
  const mapPrincipalCalendarCategoryToEventType = useMapPrincipalCalendarCategoryToEventType();
  const getRsvp = useGetRsvpFromAttandee();
  const getSplitId = useGetSplitId();

  const createEventAttendee = useCallback(
    (attendee: Attendee) => ({
      ref: attendee.id,
      role: 'attendee' as const,
      rsvp: getRsvp(attendee.partstat),
    }),
    [getRsvp],
  );

  return useCallback(
    ({ event: { data, type } }: { event: EventItem }) =>
      type === 'node' ? (
        <EventContent
          event={{
            ...data,
            author: data.organizer.id,
            id: getSplitId(data.id),
            participants: [
              ...(data.attendees?.map((attendee) => createEventAttendee(attendee)) ?? []),
              createEventModerator(data.organizer),
            ],
            status: mapPrincipalCalendarEventStatus(data.status),
            type: mapPrincipalCalendarCategoryToEventType(data.categories[0]),
          }}
        />
      ) : (
        <EventSocialContent event={data} />
      ),
    [mapPrincipalCalendarEventStatus, mapPrincipalCalendarCategoryToEventType, createEventAttendee, getSplitId],
  );
};
